import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "@/i18n";
import { Pause, Play, X } from "lucide-react";

const PHASE_LABEL = { inhale: "phase_inhale", hold_in: "phase_hold_in", exhale: "phase_exhale", hold_out: "phase_hold_out" };
const isExpanded = (k) => k === "inhale" || k === "hold_in";

// Web breathing guide — expanding/contracting orb synced to the technique's
// phase pattern, cycled `rounds` times. Mirrors the mobile SpringBreathOrb.
export default function BreathOrb({ phases, rounds, techniqueLabel, onComplete, onCancel }) {
  const t = useT();
  const flat = useMemo(() => {
    const out = [];
    for (let r = 0; r < rounds; r++) phases.forEach((p) => out.push(p));
    return out;
  }, [phases, rounds]);

  const [i, setI] = useState(0);
  const [remaining, setRemaining] = useState(phases[0]?.seconds ?? 4);
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(false);
  const startedAt = useRef(Date.now());

  const phase = flat[i] || phases[phases.length - 1];
  const round = Math.floor(i / phases.length) + 1;

  useEffect(() => {
    if (paused || done) return undefined;
    if (remaining <= 0) {
      if (i + 1 >= flat.length) {
        setDone(true);
        onComplete({ completed: true, elapsed_seconds: Math.round((Date.now() - startedAt.current) / 1000) });
        return undefined;
      }
      const ni = i + 1;
      setI(ni);
      setRemaining(flat[ni].seconds);
      return undefined;
    }
    const id = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining, paused, done, i, flat, onComplete, phases.length]);

  const expanded = isExpanded(phase.key);
  const scale = expanded ? 1 : 0.58;
  const transition = paused ? "none" : `transform ${phase.seconds}s ease-in-out`;

  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-between rounded-3xl bg-[#5f7f5b] p-8 text-white" data-testid="breath-orb">
      <div className="text-center">
        <p className="text-sm uppercase tracking-widest text-white/70">{techniqueLabel}</p>
        <p className="mt-1 text-sm text-white/80" data-testid="orb-round">{round} / {rounds}</p>
      </div>

      <div className="relative flex h-64 w-64 items-center justify-center">
        <div className="absolute h-56 w-56 rounded-full bg-white/10" style={{ transform: `scale(${scale * 1.15})`, transition }} />
        <div className="absolute h-56 w-56 rounded-full bg-white/15" style={{ transform: `scale(${scale})`, transition }} />
        <div className="absolute flex h-40 w-40 items-center justify-center rounded-full bg-white/90 text-[#3f5a3c]" style={{ transform: `scale(${scale})`, transition }}>
          <div className="text-center">
            <p className="font-display text-lg" data-testid="orb-phase">{t(PHASE_LABEL[phase.key])}</p>
            <p className="font-display text-4xl tabular-nums">{Math.max(0, remaining)}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-6">
        <button onClick={() => setPaused((p) => !p)} data-testid="orb-pause" className="flex h-14 w-14 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30">
          {paused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
        </button>
        <button onClick={() => onCancel()} data-testid="orb-stop" className="flex h-14 w-14 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30">
          <X className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
